import { instance } from "./instance";

/**
 * Token pair returned from backend
 */
export interface JwtTokenDto {
  accessToken: string;
  refreshToken: string;
  expiresAt: string; // ISO 8601 date string
}

/**
 * User info returned from token exchange
 */
export interface TokenExchangeResponse {
  token: JwtTokenDto;
  user: {
    id: string;
    identityId: string;
    name: string;
    email: string;
    userType: number;
    isWhoAdmin: boolean;
    isActive: boolean;
    isDeactivated: boolean;
  };
}

/**
 * Service for managing JWT tokens with the backend
 */
export const tokenService = {
  /**
   * Exchange Azure AD access token for app JWT
   * @param azureAdToken - Access token from Azure AD (MSAL)
   * @returns Token pair and user info
   * Note: baseURL already includes '/api', so we only need '/auth/exchange'
   * Note: Backend expects 'AzureAccessToken' property name (PascalCase)
   */
  exchangeToken: async (
    azureAdToken: string
  ): Promise<TokenExchangeResponse> => {
    const response = await instance.post<TokenExchangeResponse>(
      "/auth/exchange",
      { AzureAccessToken: azureAdToken }
    );
    return response.data;
  },

  /**
   * Refresh access token using refresh token
   * @param refreshToken - Current refresh token
   * @returns New token pair
   * Note: baseURL already includes '/api', so we only need '/auth/refresh'
   */
  refreshToken: async (refreshToken: string): Promise<JwtTokenDto> => {
    console.log("[TOKEN SERVICE] Calling refresh endpoint...");
    const response = await instance.post<JwtTokenDto>("/auth/refresh", {
      refreshToken,
    });
    console.log("[TOKEN SERVICE] Refresh endpoint returned:", {
      status: response.status,
      hasAccessToken: !!response.data.accessToken,
      hasRefreshToken: !!response.data.refreshToken,
      expiresAt: response.data.expiresAt,
    });
    return response.data;
  },

  /**
   * Logout and revoke refresh token
   * @param refreshToken - Current refresh token
   * Note: baseURL already includes '/api', so we only need '/auth/logout'
   */
  logout: async (refreshToken: string): Promise<void> => {
    try {
      await instance.post("/auth/logout", { refreshToken });
    } catch (error) {
      // Ignore errors during logout (token might already be expired/revoked)
      console.warn("Logout API call failed:", error);
    }
  },

  /**
   * Store tokens in localStorage
   */
  storeTokens: (tokens: JwtTokenDto): void => {
    localStorage.setItem("accessToken", tokens.accessToken);
    localStorage.setItem("refreshToken", tokens.refreshToken);
    localStorage.setItem("tokenExpiresAt", tokens.expiresAt);
  },

  /**
   * Get access token from localStorage
   */
  getAccessToken: (): string | null => {
    return localStorage.getItem("accessToken");
  },

  /**
   * Get refresh token from localStorage
   */
  getRefreshToken: (): string | null => {
    return localStorage.getItem("refreshToken");
  },

  /**
   * Get token expiration time from localStorage
   */
  getTokenExpiresAt: (): Date | null => {
    const expiresAt = localStorage.getItem("tokenExpiresAt");
    return expiresAt ? new Date(expiresAt) : null;
  },

  /**
   * Check if access token is expired or about to expire (within 1 minute)
   * Note: This should only be called when user has tokens (authenticated)
   */
  isTokenExpired: (): boolean => {
    const expiresAt = tokenService.getTokenExpiresAt();
    if (!expiresAt) {
      // No expiration time - token is considered expired
      // Don't log for unauthenticated users (no tokens at all)
      const hasTokens =
        tokenService.getAccessToken() || tokenService.getRefreshToken();
      if (hasTokens) {
        console.log(
          "[TOKEN SERVICE] No expiration time found, token considered expired"
        );
      }
      return true;
    }

    const now = new Date();
    const bufferTime = 60 * 1000; // 1 minute buffer
    const isExpired = now.getTime() >= expiresAt.getTime() - bufferTime;

    if (isExpired) {
      const timeUntilExpiry = expiresAt.getTime() - now.getTime();
      console.log("[TOKEN SERVICE] Token expired or about to expire:", {
        expiresAt: expiresAt.toISOString(),
        now: now.toISOString(),
        timeUntilExpiryMs: timeUntilExpiry,
        timeUntilExpiryMin: Math.floor(timeUntilExpiry / 60000),
      });
    }

    return isExpired;
  },

  /**
   * Clear all tokens from localStorage
   */
  clearTokens: (): void => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("tokenExpiresAt");
  },

  /**
   * Check if user has valid tokens
   */
  hasValidTokens: (): boolean => {
    const accessToken = tokenService.getAccessToken();
    const refreshToken = tokenService.getRefreshToken();
    return !!(accessToken && refreshToken && !tokenService.isTokenExpired());
  },
};
